import React from "react";
import { Button, Icon, Message } from "semantic-ui-react";
import { textoErrores, textoSoloLectura, useSoloLectura } from "../engine";

// Estado visible de un requisito o resultado (hoja 05): Cumple / Mejorable / Brecha parcial / Brecha mayor / Pendiente.
const CLASE_ESTADO = {
  Cumple: "ok",
  Conforme: "ok",
  Mejorable: "info",
  OM: "info",
  "Brecha parcial": "warn",
  "NC menor": "warn",
  "Brecha mayor": "danger",
  "NC mayor": "danger",
  Pendiente: "neutral",
};
export function StatusBadge({ estado, children }) {
  const clase = CLASE_ESTADO[estado] || (String(estado || "").startsWith("Pendiente") ? "neutral" : "neutral");
  return <span className={`ep-badge ep-badge--${clase}`}>{children || estado}</span>;
}

// Errores de validación (motor o servidor) con su sección y fila.
export function SaveErrors({ errores, t = (k) => k }) {
  if (!errores || !errores.length) return null;
  return (
    <Message negative className="ep-save-errors">
      <Message.Header>{t("energy_performance_not_saved_errors")}</Message.Header>
      <Message.List items={textoErrores(errores)} />
    </Message>
  );
}

// Aviso del motor (integridad, datos pendientes…).
export function Notice({ tipo = "warn", titulo, children }) {
  return (
    <div className={`ep-notice ep-notice--${tipo}`} role="note">
      {titulo && <strong className="ep-notice__title">{titulo}</strong>}
      <div>{children}</div>
    </div>
  );
}

// Barra de guardado de una sección: deshabilitada si no hay cambios o hay campos inválidos.
export function SaveBar({ dirty, invalidos, saving, onSave, onDiscard, t = (k) => k }) {
  const soloLectura = useSoloLectura();
  // sin permiso, el botón se ve deshabilitado con el motivo (hoja 23 p. 66: no se oculta); en un ejemplo, solo el aviso
  if (soloLectura === "permiso") {
    return (
      <div className="ep-savebar">
        <span className="ep-savebar__msg">{textoSoloLectura(t, soloLectura)}</span>
        <Button type="button" primary size="small" disabled title={textoSoloLectura(t, soloLectura)}><Icon name="save" /> {t("save")}</Button>
      </div>
    );
  }
  if (soloLectura) return <div className="ep-savebar"><span className="ep-savebar__msg">{textoSoloLectura(t, soloLectura)}</span></div>;
  return (
    <div className="ep-savebar">
      {invalidos > 0 && <span className="ep-savebar__msg">{t("energy_performance_fix_invalid_fields")} ({invalidos})</span>}
      {dirty && !invalidos && <span className="ep-savebar__msg ep-savebar__msg--dirty">{t("energy_performance_unsaved_changes")}</span>}
      {dirty && onDiscard && (
        <Button type="button" basic size="small" onClick={onDiscard} disabled={saving}>{t("energy_performance_discard")}</Button>
      )}
      <Button type="button" primary size="small" loading={saving} disabled={!dirty || invalidos > 0 || saving} onClick={onSave}>
        <Icon name="save" /> {t("save")}
      </Button>
    </div>
  );
}

// Guarda de las pantallas de lectura: sin instalación o con datos que el motor rechaza, muestra el motivo.
export function ConInstalacion({ installation, vista, t, children }) {
  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!vista.f) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{vista.error}</Notice>;
  return children(vista.f);
}

// Sección con título y nota (cláusula, explicación).
export function Section({ title, note, children, actions }) {
  return (
    <section className="ep-section">
      <header className="ep-section__header">
        <h4 className="ep-section__title">{title}</h4>
        {actions}
      </header>
      {note && <p className="ep-section__note">{note}</p>}
      {children}
    </section>
  );
}
